import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import {
  ChevronDown, ChevronUp, ChevronsUpDown, Filter,
  Settings2, Eye, EyeOff, Plus,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

// ===== Types =====
import {
  aggregateValues, formatValue, SortIcon, ColumnSettings, ColumnFilterInput, useBreakpoint,
  type DataTableColumn, type DataTableFilter, type DataTableAction, type DataTableProps,
} from './dataTableParts';
export type { DataTableColumn, DataTableFilter, DataTableAction, DataTableProps } from './dataTableParts';
export function DataTable({
  columns,
  data,
  rowKey = 'id',
  filters: externalFilters,
  onFilterChange,
  pageSize = 50,
  totalRows,
  onLoadMore,
  loading = false,
  showFooter = true,
  sortable = true,
  striped = true,
  compact = false,
  caption,
  emptyText = 'Нет данных',
  onRowClick,
  onRowDoubleClick,
  rowClassName,
  rowStyle,
  selectable = false,
  noCheckboxes = false,
  selectedKeys,
  onSelectionChange,
  selectionActive: selectionActiveProp,
  onLongPress,
  hideToolbar = false,
  hideSearch = false,
  showFiltersExternal,
  onShowFiltersExternal,
  columnVisibilityExternal,
  onColumnVisibilityExternal,
  actionBar,
}: DataTableProps) {
  const bp = useBreakpoint();
  const tableRef = useRef<HTMLDivElement>(null);

  // Column visibility based on breakpoint
  const [internalVisibility, setInternalVisibility] = useState<Record<string, boolean>>({});
  const manualVisibility = columnVisibilityExternal ?? internalVisibility;
  const setManualVisibility = onColumnVisibilityExternal
    ? (v: Record<string, boolean> | ((prev: Record<string, boolean>) => Record<string, boolean>)) => {
        const next = typeof v === 'function' ? v(manualVisibility) : v;
        onColumnVisibilityExternal(next);
      }
    : setInternalVisibility;
  const [searchQuery, setSearchQuery] = useState('');

  const visibleColumns = useMemo(() => {
    return columns.filter((col) => {
      // Manual override
      if (manualVisibility[col.key] === false) return false;
      // Responsive default
      const vis = col.visibility;
      if (!vis) return true; // visible everywhere by default
      switch (bp) {
        case 'mobile': return vis.mobile !== false;
        case 'tablet': return vis.tablet !== false;
        case 'desktop': return vis.desktop !== false;
        default: return true;
      }
    });
  }, [columns, bp, manualVisibility]);

  // Sorting
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const handleSort = useCallback((key: string) => {
    if (!sortable) return;
    setSortKey((prev) => {
      if (prev === key) {
        setSortDir((d) => d === 'asc' ? 'desc' : 'asc');
        return key;
      }
      setSortDir('asc');
      return key;
    });
  }, [sortable]);

  // Filters
  const [filterValues, setFilterValues] = useState<Record<string, unknown>>({});

  const handleFilterChange = useCallback((key: string, value: unknown) => {
    setFilterValues((prev) => {
      const next = { ...prev };
      if (value === undefined || value === '') {
        delete next[key];
      } else {
        next[key] = value;
      }
      onFilterChange?.(next);
      return next;
    });
  }, [onFilterChange]);

  const [internalShowFilters, setInternalShowFilters] = useState(true);
  const showFilters = showFiltersExternal ?? internalShowFilters;
  const toggleShowFilters = () => {
    const next = !showFilters;
    if (onShowFiltersExternal) onShowFiltersExternal(next);
    else setInternalShowFilters(next);
  };

  // Process data: filter → sort
  const processedData = useMemo(() => {
    let result = [...data];

    // Поиск по всем колонкам
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter((row) =>
        Object.values(row).some((val) =>
          String(val ?? '').toLowerCase().includes(query)
        )
      );
    }

    // Filter
    for (const [key, value] of Object.entries(filterValues)) {
      if (value === undefined) continue;
      const strVal = String(value).toLowerCase();
      result = result.filter((row) => {
        const cellVal = String(row[key] ?? '').toLowerCase();
        return cellVal.includes(strVal);
      });
    }

    // Sort
    if (sortKey) {
      result.sort((a, b) => {
        const aVal = a[sortKey];
        const bVal = b[sortKey];
        const cmp = String(aVal ?? '').localeCompare(String(bVal ?? ''), 'ru-RU', { numeric: true });
        return sortDir === 'asc' ? cmp : -cmp;
      });
    }

    return result;
  }, [data, filterValues, sortKey, sortDir, searchQuery]);

  // Infinite scroll state
  const [visibleCount, setVisibleCount] = useState(pageSize);
  const visibleData = useMemo(() => {
    return processedData.slice(0, visibleCount);
  }, [processedData, visibleCount]);

  // Reset visible count on filter/sort/data change
  useEffect(() => { setVisibleCount(pageSize); }, [filterValues, sortKey, sortDir, data, pageSize]);

  // Sentinel ref for IntersectionObserver
  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && visibleCount < processedData.length) {
          setVisibleCount((prev) => Math.min(prev + pageSize, processedData.length));
        }
      },
      { root: tableRef.current, threshold: 0.1 }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [visibleCount, processedData.length, pageSize]);

  // Toggle column visibility
  const toggleColumn = useCallback((key: string) => {
    setManualVisibility((prev) => ({
      ...prev,
      [key]: prev[key] === false ? true : false,
    }));
  }, []);

  // Selection
  const [internalSelected, setInternalSelected] = useState<Set<string | number>>(new Set());
  const selected = selectedKeys ?? internalSelected;
  // Выделение: НЕ всегда активно. Включается удержанием (long-press) или чекбоксом
  // Режим выделения: внешний (из AutoListForm — единый для всех видов) или внутренний.
  const [selectionEnabled, setSelectionEnabled] = useState(selectable && !noCheckboxes);
  const selectionActive = selectionActiveProp !== undefined ? selectionActiveProp : selectionEnabled;
  const [lastTapTime, setLastTapTime] = useState(0);

  const toggleRow = useCallback((key: string | number) => {
    const next = new Set(selected);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setInternalSelected(next);
    onSelectionChange?.(next);
  }, [selected, onSelectionChange]);

  // УДЕРЖАНИЕ (500мс) — включить режим выделения (внешний колбэк) и отметить строку;
  // короткий клик — обычное действие (в т.ч. открыть объект).
  const pressTimer = useRef<number | null>(null);
  const longPressFired = useRef(false);
  const startRowPress = useCallback((key: string | number) => {
    longPressFired.current = false;
    if (pressTimer.current) clearTimeout(pressTimer.current);
    pressTimer.current = window.setTimeout(() => {
      longPressFired.current = true;
      onLongPress?.();
      setSelectionEnabled(true);
      toggleRow(key);
    }, 500);
  }, [toggleRow, onLongPress]);
  const cancelRowPress = useCallback(() => {
    if (pressTimer.current) { clearTimeout(pressTimer.current); pressTimer.current = null; }
  }, []);

  const hasActiveFilters = Object.keys(filterValues).length > 0;

  return (
    <div className="flex flex-col h-full border rounded-lg overflow-hidden bg-card text-sm">
      {/* Toolbar (скрыт если hideToolbar) */}
      {!hideToolbar && (
        <div className="flex items-center justify-between px-3 py-2 border-b bg-muted/30 shrink-0">
          <div className="flex items-center gap-2">
            {caption && <span className="text-xs font-medium text-muted-foreground">{caption}</span>}
            {totalRows != null && (
              <span className="text-xs text-muted-foreground">
                {totalRows.toLocaleString('ru-RU')} записей
              </span>
            )}
            {hasActiveFilters && (
              <span className="text-xs bg-primary/10 text-primary px-1.5 py-0.5 rounded">
                Фильтры активны
              </span>
            )}
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant={showFilters ? 'secondary' : 'ghost'}
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={toggleShowFilters}
            >
              <Filter className="h-3 w-3 mr-1" />
              Фильтры
            </Button>
            <ColumnSettings columns={columns} visibility={manualVisibility} onToggle={toggleColumn} />
          </div>
        </div>
      )}

      {/* Filters row */}
      {showFilters && externalFilters && externalFilters.length > 0 && (
        <div className="flex flex-wrap gap-2 px-3 py-2 border-b bg-muted/20 shrink-0">
          {externalFilters.map((f) => (
            <div key={f.key} className="flex flex-col gap-0.5">
              <label className="text-[10px] text-muted-foreground">{f.label}</label>
              <ColumnFilterInput
                filter={f}
                value={filterValues[f.key]}
                onChange={(val) => handleFilterChange(f.key, val)}
              />
            </div>
          ))}
        </div>
      )}

      {/* Action bar */}
      {actionBar?.visible !== false && (
        <div className="flex items-center gap-2 px-3 py-2 border-b shrink-0">
          {actionBar?.showCreate && (
            <Button size="sm" className="h-7 text-xs" onClick={actionBar.onCreate}>
              <Plus className="h-3 w-3 mr-1" />Создать
            </Button>
          )}
          {actionBar?.showAdd && (
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={actionBar.onAdd}>
              <Plus className="h-3 w-3 mr-1" />Добавить
            </Button>
          )}
          {actionBar?.showCopy && selected && selected.size > 0 && (
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => {
              const selectedRows = data.filter((r) => selected.has(r[rowKey as keyof typeof r] as string | number));
              actionBar.onCopy?.(selectedRows);
            }}>
              Копировать ({selected.size})
            </Button>
          )}
          {actionBar?.customActions?.map((action, idx) => (
            <Button
              key={idx}
              size="sm"
              variant="outline"
              className="h-7 text-xs"
              onClick={() => {
                const selectedRows = data.filter((r) => selected.has(r[rowKey as keyof typeof r] as string | number));
                action.onClick(selectedRows);
              }}
              disabled={action.requiresSelection && (!selected || selected.size === 0)}
            >
              {action.label}
            </Button>
          ))}
        </div>
      )}

      {/* Search bar */}
      {!hideSearch && (
        <div className="px-3 py-2 border-b shrink-0">
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Поиск..."
            className="h-7 text-xs"
          />
        </div>
      )}

      {/* Table */}
      <div ref={tableRef} className="relative flex-1 overflow-auto min-h-0">
        <table className="w-full text-sm border-collapse">
          {/* Fixed header */}
          <thead className="sticky top-0 z-10 bg-muted/70 backdrop-blur-sm">
            <tr className="border-b">
              {selectable && !noCheckboxes && (
                <th className="px-3 py-2 w-8">
                  <input type="checkbox" checked={selectionEnabled} onChange={() => setSelectionEnabled(!selectionEnabled)} className="rounded" title="Режим выделения" />
                </th>
              )}
              {visibleColumns.map((col) => (
                <th
                  key={col.key}
                  className={cn(
                    'px-3 py-2 text-xs font-medium text-left border-r last:border-r-0 whitespace-nowrap select-none',
                    sortable && col.sortable !== false && 'cursor-pointer',
                    col.align === 'right' && 'text-right',
                    col.align === 'center' && 'text-center',
                  )}
                  style={{
                    ...(col.width ? { width: col.width, minWidth: col.width } : {}),
                    ...(col.maxWidth ? { maxWidth: col.maxWidth } : {}),
                  }}
                  onClick={() => col.sortable !== false && handleSort(col.key)}
                >
                  <div className="flex items-center gap-1">
                    <span>{col.label}</span>
                    {sortable && col.sortable !== false && (
                      <SortIcon direction={sortKey === col.key ? sortDir : null} />
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {visibleData.map((row, rowIdx) => {
              // Детерминированный key — никаких fallback'ов.
              // Если ключ не определён — это БАГ в коде (неправильный rowKey).
              // Логируем ошибку и пропускаем строку, чтобы не падал весь список.
              const key = row[rowKey] as string | number;
              if (key === undefined || key === null || key === '') {
                console.error(
                  `[DataTable] Row ${rowIdx} has no key field "${rowKey}". ` +
                  `Available fields: ${Object.keys(row).join(', ')}. ` +
                  `Set correct rowKey prop (e.g. 'id' for Center, 'Ссылка' for 1С).`
                );
                return null;
              }
              const selectedRow = selected.has(key);
              // ВЫДЕЛЕНИЕ (темизируемое, БЕЗ заливки и БЕЗ колец на каждой ячейке):
              // акцентная полоска слева на первой ячейке + текст цветом темы.
              const baseStyle = rowStyle?.(row) || {};
              const leftBar = selectedRow ? { boxShadow: 'inset 3px 0 0 0 var(--color-primary)' } : undefined;
              return (
                <tr
                  key={key ?? rowIdx}
                  style={baseStyle}
                  className={cn(
                    'border-b last:border-b-0 transition-colors duration-150',
                    striped && rowIdx % 2 === 1 && 'bg-muted/20',
                    (onRowClick || onRowDoubleClick) && 'cursor-pointer',
                    'hover:bg-accent/5',
                    selectedRow && 'text-primary font-medium',
                    rowClassName?.(row),
                  )}
                  onClick={(e) => {
                    // После удержания (long-press) клик подавляем — выделение уже переключено
                    if (longPressFired.current) { longPressFired.current = false; return; }
                    if ((e.target as HTMLElement).tagName === 'INPUT') return;
                    // КЛИК ВСЕГДА открывает объект (удержание — переключение выделения)
                    // На мобильных — ручной детект двойного тапа
                    if (window.innerWidth < 640 && onRowDoubleClick) {
                      const tr = e.currentTarget;
                      const lastTap = Number(tr.dataset.lastTap || '0');
                      const now = Date.now();
                      if (now - lastTap < 400) {
                        // Двойной тап → открыть форму
                        tr.dataset.lastTap = '0';
                        onRowDoubleClick?.(row);
                        return;
                      }
                      tr.dataset.lastTap = String(now);
                    }
                    onRowClick?.(row);
                  }}
                  onMouseDown={() => startRowPress(key)}
                  onTouchStart={() => startRowPress(key)}
                  onMouseUp={cancelRowPress}
                  onTouchEnd={cancelRowPress}
                  onMouseLeave={cancelRowPress}
                  onDoubleClick={(e) => {
                    if ((e.target as HTMLElement).tagName === 'INPUT') return;
                    onRowDoubleClick?.(row);
                  }}
                >
                  {selectable && !noCheckboxes && (
                    <td className="px-3 py-1.5 w-8" style={leftBar} onClick={(e) => e.stopPropagation()}>
                      {selectionActive && (
                        <input type="checkbox" checked={selected.has(key)} onChange={() => toggleRow(key)} className="rounded" />
                      )}
                    </td>
                  )}
                  {visibleColumns.map((col, ci) => {
                    const isFirst = !(selectable && !noCheckboxes) && ci === 0;
                    return (
                      <td
                        key={col.key}
                        className={cn(
                          compact ? 'px-2 py-1' : 'px-3 py-1.5',
                          'border-r last:border-r-0 whitespace-nowrap overflow-hidden text-ellipsis',
                          col.align === 'right' && 'text-right',
                          col.align === 'center' && 'text-center',
                        )}
                        style={{
                          ...(col.maxWidth ? { maxWidth: col.maxWidth } : col.width ? { maxWidth: col.width } : undefined),
                          ...(selectedRow && isFirst ? leftBar : {}),
                        }}
                        title={String(row[col.key] ?? '')}
                      >
                        {col.render
                          ? col.render(row[col.key], row)
                          : formatValue(row[col.key], col.format)}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
            {/* Infinite scroll sentinel */}
            {visibleData.length < processedData.length && (
              <tr>
                <td colSpan={visibleColumns.length} className="p-0">
                  <div ref={sentinelRef} className="h-4" />
                </td>
              </tr>
            )}
          </tbody>

          {/* Footer with aggregations */}
          {showFooter && visibleData.length > 0 && visibleColumns.some((c) => c.aggregate && c.aggregate !== 'none') && (
            <tfoot className="sticky bottom-0 z-10 bg-muted/70 backdrop-blur-sm border-t-2">
              <tr className="font-medium">
                {visibleColumns.map((col) => {
                  const agg = col.aggregate;
                  if (!agg || agg === 'none') {
                    return <td key={col.key} className="px-3 py-2 border-r last:border-r-0" />;
                  }
                  const values = visibleData.map((r) => r[col.key]);
                  return (
                    <td
                      key={col.key}
                      className={cn(
                        'px-3 py-2 border-r last:border-r-0 text-xs font-medium',
                        col.align === 'right' && 'text-right',
                        col.align === 'center' && 'text-center',
                      )}
                    >
                      <span className="text-primary">
                        {agg === 'count' ? values.length : aggregateValues(values, agg)}
                      </span>
                    </td>
                  );
                })}
              </tr>
            </tfoot>
          )}
        </table>
        {/* Пусто: сетка продолжается, поверх — сообщение */}
        {visibleData.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center text-muted-foreground text-sm pointer-events-none px-3">
            {loading ? (
              <div className="flex items-center justify-center gap-2">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                Загрузка...
              </div>
            ) : (
              emptyText
            )}
          </div>
        )}
      </div>

      {/* Infinite scroll status bar */}
      <div className="flex items-center justify-between px-3 py-2 border-t bg-muted/60 text-xs shrink-0">
        <span className="text-muted-foreground">
          {visibleData.length.toLocaleString('ru-RU')} из{' '}
          {processedData.length.toLocaleString('ru-RU')}
          {processedData.length !== data.length && ` (фильтр)`}
          {loading && ' · загрузка...'}
        </span>
        {onLoadMore && visibleData.length < processedData.length && (
          <Button
            variant="ghost"
            size="sm"
            className="h-6 px-2 text-xs"
            onClick={onLoadMore}
            disabled={loading}
          >
            Загрузить ещё
          </Button>
        )}
      </div>
    </div>
  );
}
